import { ConesIllustration, NoEntryIllustration } from '@dcs/ui/illustrations';
import { useEffect, useRef } from 'react';
import { isRouteErrorResponse, Link, useRouteError } from 'react-router';
import { t } from '../messages/vi';
import { usePorts } from '../providers';
import { rt } from './route-error.messages';
import './route-error.css';

export interface RouteErrorProps {
  /**
   * True when the screen is drawn without the app frame (`/drive*`, `/exam*`): it then brings its
   * own `<main>` landmark. Inside the shell it sits in the shell's `<main>` and must not add one.
   */
  readonly frameless?: boolean;
}

/** Router-level error screen: unknown paths (404) and errors thrown while rendering a route. */
export function RouteError({ frameless = false }: RouteErrorProps) {
  const error = useRouteError();
  const { errorReporter } = usePorts();
  const notFound = isRouteErrorResponse(error) && error.status === 404;

  // Once per error: StrictMode runs effects twice, and a re-render must not report again.
  const reported = useRef<{ readonly error: unknown } | null>(null);
  useEffect(() => {
    if (notFound || reported.current?.error === error) return;
    reported.current = { error };
    errorReporter.report(error, { source: 'route' });
  }, [error, errorReporter, notFound]);

  const body = (
    <div className="route-error" role={notFound ? undefined : 'alert'}>
      {notFound ? (
        <NoEntryIllustration className="route-error__art" />
      ) : (
        <ConesIllustration className="route-error__art" />
      )}
      <h1 className="route-error__title">
        {notFound ? t('error.notFound') : rt('routeError.title')}
      </h1>
      <p className="route-error__lead">
        {notFound ? rt('routeError.notFoundLead') : t('error.generic')}
      </p>
      <div className="route-error__actions">
        {notFound ? null : (
          <button
            type="button"
            className="dcs-button dcs-button--primary"
            onClick={() => window.location.reload()}
          >
            {rt('routeError.retry')}
          </button>
        )}
        <Link to="/" className={`dcs-button${notFound ? ' dcs-button--primary' : ''}`}>
          {t('common.back')}
        </Link>
      </div>
    </div>
  );

  return frameless ? (
    <main className="route-error-page">{body}</main>
  ) : (
    <div className="app-page route-error-page">{body}</div>
  );
}
